import React, { useState, useEffect, useCallback, useMemo } from "react";
import Spin from "antd/es/spin";
import LocationSettingsService from "services/locationSettings";
import { useLocationSelect } from "hooks/useSelect";

import { Title } from "components/Text";
import { Row, Col } from "components/layout";
import Button from "components/Button";
import SettingsList from "../components/SettingsList";

const locationSettingsSchema = [
  {
    name: "location_name",
    inputType: "text",
    title: "Location Name",
    readOnly: true,
  },
  { name: "code", inputType: "text", title: "Code", readOnly: true },
  { name: "tz_name", inputType: "text", title: "Time Zone" },
  { name: "yms_email", inputType: "email", title: "YMS Email Address" },
  { name: "yms_alerts", inputType: "switch", title: "Enable YMS Alerts" },
  {
    name: "yms_per_hour",
    inputType: "number",
    title: "Expected YMS Emails per Hour",
  },
  { name: "wms_email", inputType: "email", title: "WMS Email Address" },
  { name: "wms_alerts", inputType: "switch", title: "Enable WMS Alerts" },
  {
    name: "wms_per_month",
    inputType: "number",
    title: "Expected WMS Emails per Month",
  },
  { name: "staff_email", inputType: "email", title: "Staff Email Address" },
  {
    name: "use_load_active_cph",
    inputType: "switch",
    title: "Use Active Time for CPH Calculation",
  },
  {
    name: "segment_shift_work",
    inputType: "switch",
    title: "Segment Shift Work",
  },
  { name: "notes", inputType: "textarea", title: "Notes" },
];

const locationSettingsService = new LocationSettingsService();

const LocationTab = ({ showMessage }) => {
  const { value: selectedLocationId } = useLocationSelect();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [settingsValues, setSettingsValues] = useState([]);

  const modifiedSettingsValues = useMemo(() => {
    return settingsValues.filter((settingValue) => !!settingValue.modified);
  }, [settingsValues]);

  useEffect(() => {
    if (!selectedLocationId) return;
  
    setIsLoading(true);
  
    locationSettingsService
      .getLocationSettings(selectedLocationId)
      .then((data) => {
        const mappedData = locationSettingsSchema.map((schemaItem) => {
          const apiItem = data.find((item) =>
            item.hasOwnProperty(schemaItem.name)
          );
          return {
            id: apiItem ? apiItem.id : undefined,
            name: schemaItem.name,
            value: apiItem ? apiItem[schemaItem.name] : "",
            readOnly: schemaItem.readOnly || false,
            modified: false,
          };
        });
  
        setSettingsValues(mappedData);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedLocationId]);

  const onSaveChanges = useCallback(() => {
    setIsSubmitting(true);

    const updates = settingsValues.reduce(
      (acc, setting) => {
        acc[setting.name] = setting.value;
        return acc;
      },
      { location_id: selectedLocationId }
    );

    locationSettingsService
      .updateLocationSettings(updates)
      .then(() => {
        showMessage("success", "Location settings updated");
        setSettingsValues((settings) =>
          settings.map((config) => {
            config.modified = false;
            return config;
          })
        );
      })
      .catch(() => {
        showMessage("error", "Failed to save location settings");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [showMessage, settingsValues, selectedLocationId]);

  if (isLoading) {
    return <Spin size={30} style={{ width: "100%", padding: "36px" }} />;
  }

  return (
    <Row justify="center">
      <Col span={18}>
        <Title level={4} style={{ textAlign: "center", marginBottom: "32px" }}>
          Location
        </Title>

        <SettingsList
          settingsSchema={locationSettingsSchema}
          settingsValues={settingsValues}
          setSettingsValues={setSettingsValues}
        />

        <Row justify="center" style={{ padding: "16px 0" }}>
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={modifiedSettingsValues.length === 0}
            size="large"
            onClick={onSaveChanges}
            style={{ width: "350px" }}
          >
            Save Changes
          </Button>
        </Row>
      </Col>
    </Row>
  );
};

export default LocationTab;
